import { useLayoutEffect, useRef, useState } from 'react';

// Same reading policy as Messenger; all positioning stays inside this viewport.
const BOTTOM_EPS = 60;
const WINDOW_SIZE = 10;
const identities = (message) => [
  message._id && `id:${message._id}`,
  message.tempId && `pending:${message.senderId}:${message.tempId}`,
].filter(Boolean);

export const galaxyScrollIdentity = (message) => JSON.stringify(identities(message));

export default function useGalaxyChatScroll({
  messages, scope, active, renderingMessages, viewportRef, contentRef,
}) {
  const [reveal, setReveal] = useState(0);
  const [away, setAway] = useState(false);
  const windowRef = useRef(null);
  const controllerRef = useRef(null);
  const renderingRef = useRef(renderingMessages);
  renderingRef.current = renderingMessages;
  const committedWindow = windowRef.current;
  let start = Math.max(0, messages.length - WINDOW_SIZE);

  // Keep the oldest surviving rendered identity, rather than a moving last-N
  // slice. Only identities are retained; message content remains parent-owned.
  if (active && committedWindow?.scope === scope) {
    const indices = new Map();
    messages.forEach((message, index) => identities(message).forEach((id) => indices.set(id, index)));
    const surviving = committedWindow.rows.find((ids) => ids.some((id) => indices.has(id)));
    if (surviving) start = indices.get(surviving.find((id) => indices.has(id)));
    start = Math.max(0, start - (reveal - committedWindow.reveal) * WINDOW_SIZE);
  }

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!active || !viewport || !content) {
      controllerRef.current = null;
      setAway(false);
      return undefined;
    }
    let live = true;
    let following = true;
    let anchors = [];
    let lastTop = viewport.scrollTop;

    const rows = () => Array.from(content.querySelectorAll('[data-galaxy-scroll-id]'));
    const offset = (node) => node.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
    const remember = () => {
      const nodes = rows();
      const first = nodes.findIndex((node) => node.getBoundingClientRect().bottom >
        viewport.getBoundingClientRect().top);
      anchors = nodes.map((node, index) => ({
        ids: JSON.parse(node.dataset.galaxyScrollId),
        offset: offset(node),
        // Prefer the next row on a tie: the preceding row may be entirely
        // above the viewport when the visible anchor itself was deleted.
        distance: Math.abs(index - Math.max(0, first)) * 2 + (index < first ? 1 : 0),
      })).sort((a, b) => a.distance - b.distance);
      lastTop = viewport.scrollTop;
    };
    const sync = () => {
      if (!live) return;
      // A temporary loading/error presentation must not replace the reading
      // snapshot with an empty DOM. No message content is retained here.
      if (!renderingRef.current) {
        lastTop = viewport.scrollTop;
        return;
      }
      // Following initializes once per room/viewer/access scope, even for
      // empty history. Query loading/success/retry never resets reader intent.
      if (following) {
        viewport.scrollTop = viewport.scrollHeight;
        remember();
      } else {
        const byId = new Map();
        rows().forEach((node) => JSON.parse(node.dataset.galaxyScrollId)
          .forEach((id) => byId.set(id, node)));
        const anchor = anchors.find(({ ids }) => ids.some((id) => byId.has(id)));
        if (anchor) {
          const node = byId.get(anchor.ids.find((id) => byId.has(id)));
          viewport.scrollTop += offset(node) - anchor.offset;
          // Keep the original target through successive child layouts. Taking
          // a new snapshot after every correction accumulates scrollTop's
          // fractional-pixel rounding. Rebase only when the viewport clamps.
          if (Math.abs(offset(node) - anchor.offset) > 1) remember();
        } else {
          remember();
        }
        lastTop = viewport.scrollTop;
      }
      setAway(!following && viewport.scrollHeight - viewport.clientHeight > BOTTOM_EPS);
    };
    const onScroll = () => {
      if (!live || !renderingRef.current || Math.abs(viewport.scrollTop - lastTop) < 1) return;
      following = viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= BOTTOM_EPS;
      remember();
      setAway(!following);
    };
    const latest = () => {
      if (!live) return;
      following = true;
      sync();
    };
    const revealOlder = () => {
      following = false;
      remember();
    };
    controllerRef.current = { scope, sync, latest, revealOlder };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => sync());
    observer?.observe(viewport);
    observer?.observe(content);
    return () => {
      live = false;
      observer?.disconnect();
      viewport.removeEventListener('scroll', onScroll);
      controllerRef.current = null;
    };
  }, [scope, active, viewportRef, contentRef]);

  useLayoutEffect(() => {
    windowRef.current = active ? {
      scope, reveal, rows: messages.slice(start).map(identities),
    } : null;
    controllerRef.current?.sync();
  });

  const jumpToLatest = () => {
    if (controllerRef.current?.scope === scope) controllerRef.current.latest();
  };
  const showMore = () => {
    // Capture immediately before prepending, including a scroll in this turn.
    controllerRef.current?.revealOlder();
    setReveal((value) => value + 1);
  };

  return { start, showMore, jumpToLatest, away: active && away };
}
